import { useEffect, useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ExpandableContainer, { Item } from './ExpandableContainer';
import { NavGraph } from 'src/types/navGraph';
import { useNavigate, useNavigationType, useLocation } from 'react-router';
import LoadingSpinner from './LoadingSpinner';
import { useArtifactDetails } from './providers/ArticleDetailsProvider';
import { SharedLinkInput } from 'src/types/sharedLink';
import DynamicLayout from './DynamicLayout';
import { useTranslate } from 'src/i18n/useTranslate';
import { usePreferences } from './providers/PreferencesProvider';
import {
    getFilteredNavGraph,
    generateFilter,
    getItemByPath,
    makeInitialFilter,
} from 'src/utils/hierarchyUtils';
import { Box } from '@cloudscape-design/components';
import { useNavGraph } from './providers/NavGraphProvider';
import { useRecentlyViewed } from './providers/RecentlyViewedProvider';
import { openExternalUrl } from 'src/helpers';
import { useBreadCrumbFilters } from './providers/BreadCrumbFilterProvider';

export interface FilterItem {
    title: string;
    itemSelected: string | null;
    items: Item[];
}

interface HierarchyFiltersProps {
    setExploreModalOpen?: (isOpen: boolean) => void;
    modalVersion?: boolean;
    filterToggleChecked: boolean;
}

const HierarchyFilters = ({
    modalVersion = false,
    setExploreModalOpen,
    filterToggleChecked,
}: HierarchyFiltersProps) => {
    const t = useTranslate();

    const [filters, setFilters] = useState<FilterItem[]>([]);

    const [showingDynamicLayout, setShowingDynamicLayout] =
        useState<boolean>(false);
    const [dynamicLayoutIsArtifact, setDynamicLayoutIsArtifact] =
        useState<boolean>(false);
    const [dynamicDrilldownOptions, setDynamicDrilldownOptions] =
        useState<FilterItem>();
    const [dynamicLayoutTitle, setDynamicLayoutTitle] = useState<string>();
    const [dynamicArtifactId, setDynamicArtifactId] = useState<string>();
    const [dynamicArtifactImage, setDynamicArtifactImage] = useState<string>();
    const [dynamicArtifactVersionId, setDynamicArtifactVersionId] =
        useState<string>();

    const { navGraph, isLoading, hasError } = useNavGraph();
    const { setArtifactDetails } = useArtifactDetails();
    const { addRecentlyViewed } = useRecentlyViewed();
    const { breadCrumbFilters, tabContext } = useBreadCrumbFilters();
    const navigationType = useNavigationType();
    const location = useLocation();

    const { userPreferences } = usePreferences();

    const filteredGraph = structuredClone(navGraph) as NavGraph;
    getFilteredNavGraph(filteredGraph, userPreferences, filterToggleChecked);

    const navigate = useNavigate();

    const handleItemSelection = ({
        path,
        documentId,
        versionId,
        externalLinkUrl,
        isDynamic,
        dateUpdated,
        title,
    }: SharedLinkInput) => {
        if (!isDynamic) {
            setShowingDynamicLayout(false);
        }

        if (path) {
            const tabValue = path.split('/').filter(Boolean).pop();
            if (tabValue) {
                if (modalVersion) {
                    const baseHash = location.hash.split('?')[0];
                    window.location.hash = `${baseHash}?explore=true&tab=${tabValue}`;
                } else {
                    navigate(`?tab=${tabValue}`);
                }
            }
        }

        if (externalLinkUrl?.length) {
            handleExternalLinkSelection(externalLinkUrl);
            documentId && addRecentlyViewed(documentId);
        } else if (isDynamic) {
            handleDynamicSelection({ documentId, versionId, path, title });
        } else if (documentId) {
            handleArtifactSelection({
                path,
                documentId,
                externalLinkUrl,
                isDynamic,
                dateUpdated,
                title,
            });
        } else if (path?.length) {
            handleDrillDownNavigation(path);
        }
    };

    const handleExternalLinkSelection = (externalLinkUrl: string) => {
        openExternalUrl(externalLinkUrl);
    };

    const handleDynamicSelection = ({
        documentId,
        versionId,
        path,
        title,
    }: {
        documentId?: number;
        versionId?: number;
        path?: string;
        title?: string;
    }) => {
        setShowingDynamicLayout(true);

        const prevFilters = [...filters];
        const lastFilter = filters[filters.length - 1];
        const selectedItem = lastFilter.items.find(
            (s: Item) => s.documentId == documentId || s.path === path
        );

        prevFilters[prevFilters.length - 1].itemSelected =
            selectedItem?.title || title || null;
        setFilters(prevFilters);

        if (documentId) {
            // handle dynamic artifact selection here
            setDynamicLayoutIsArtifact(true);
            setDynamicLayoutTitle(title || selectedItem?.title);
            setDynamicArtifactId(`${documentId}`);
            setDynamicArtifactVersionId(String(versionId));
        } else if (path?.length) {
            // handle dynamic nav drilldown selection here
            const navItem = getItemByPath(path, filteredGraph);
            const options = generateFilter(navItem);
            setDynamicDrilldownOptions(options);
            setDynamicLayoutIsArtifact(false);
            setDynamicLayoutTitle(navItem.name);
            setDynamicArtifactImage(navItem.pictureUrl);
            setDynamicArtifactId('');
            setDynamicArtifactVersionId('');
        }
    };

    const handleDrillDownNavigation = (path: string) => {
        const navItem = getItemByPath(path, filteredGraph);
        const prevFilters = [...filters];

        prevFilters[prevFilters.length - 1].itemSelected = navItem?.name;
        prevFilters[prevFilters.length - 1].title =
            navItem?.name || prevFilters[prevFilters.length - 1].title;
        prevFilters.push(generateFilter(navItem));

        setFilters(prevFilters);
    };

    const handleArtifactSelection = (documentInfo: SharedLinkInput) => {
        if (setArtifactDetails) {
            setArtifactDetails(filters, documentInfo);
        }

        navigate(`/${documentInfo.documentId}`);

        if (modalVersion && setExploreModalOpen) {
            setExploreModalOpen(false);
        }
    };

    const handleExpandChange = (index: number) => {
        setShowingDynamicLayout(false);
        if (index !== filters.length - 1) {
            const prevFilters = [...filters].slice(0, index + 1);
            prevFilters[index].itemSelected = null;

            if (index === 0) {
                prevFilters[0].title = 'Home';
            }

            setFilters(prevFilters);

            if (index > 0) {
                const previousFilter = prevFilters[index - 1];
                const selectedItem = previousFilter.items.find(
                    (item) => item.title === previousFilter.itemSelected
                );
                if (selectedItem) {
                    const itemKey =
                        selectedItem.path?.split('/').filter(Boolean).pop() ||
                        String(selectedItem.documentId);
                    if (modalVersion) {
                        const baseHash = location.hash.split('?')[0];
                        window.location.hash = `${baseHash}?explore=true&tab=${itemKey}`;
                    } else {
                        navigate(`?tab=${itemKey}`);
                    }
                }
            }
        }
    };

    useEffect(() => {
        if (
            filteredGraph &&
            Object.keys(filteredGraph).length &&
            !filters.length
        ) {
            setShowingDynamicLayout(false);
            setFilters(makeInitialFilter(filteredGraph));
        }
    }, [navGraph, filterToggleChecked, userPreferences]);

    useEffect(() => {
        if (breadCrumbFilters?.length) {
            setFilters([...breadCrumbFilters]);
        }
    }, [breadCrumbFilters]);

    useEffect(() => {
        if (navigationType === 'POP') {
            const currentTab = location.hash.includes('tab=')
                ? location.hash.split('tab=')[1].split('&')[0]
                : null;
            if (currentTab && !tabContext.get(currentTab)?.isDynamic) {
                setShowingDynamicLayout(false);
            }
        }
    }, [navigationType, location.hash, tabContext]);

    if (isLoading)
        return (
            <LoadingSpinner
                message={`${t(
                    'navigation_loading_message',
                    'Loading Navigation Filters'
                )}...`}
            />
        );
    if (hasError || !filters.length) return null;

    return filterToggleChecked && !Object.keys(filteredGraph || {}).length ? (
        <Box variant="h3" textAlign="center">
            {t(
                'navigation_empty',
                "Your current user preference filters aren't returning any document results. Try adjusting your role, language, or region."
            )}
        </Box>
    ) : (
        <SpaceBetween size="m">
            {filters.map((filter, index) => (
                <ExpandableContainer
                    key={`${filter.title}-${index}`}
                    items={filter.items}
                    headerText={filter.title}
                    expanded={
                        index === filters.length - 1 && !showingDynamicLayout
                    }
                    selection={filter.itemSelected}
                    handleExpandChange={() => handleExpandChange(index)}
                    handleItemSelection={handleItemSelection}
                />
            ))}
            {showingDynamicLayout && (
                <DynamicLayout
                    title={dynamicLayoutTitle}
                    dynamicDrilldownOptions={dynamicDrilldownOptions}
                    dynamicLayoutIsArtifact={dynamicLayoutIsArtifact}
                    dynamicArtifactImage={dynamicArtifactImage}
                    artifactId={dynamicArtifactId}
                    filters={filters}
                    modalVersion={modalVersion}
                    setExploreModalOpen={setExploreModalOpen}
                    handleDynamicItemSelection={handleDynamicSelection}
                />
            )}
        </SpaceBetween>
    );
};

export default HierarchyFilters;
